<template>
    <div class="config-margin-group">
        <div class="config-margin-card rounder-8 mb-4">
            <div class="filters-summary-header">
                <span class="filters-summary-icon">
                    <font-awesome-icon :icon="['fas', 'filter']" />
                </span>
                <span class="filters-summary-title">Filtros de productos:</span>
            </div>

            <div class="col-12 mb-3" v-if="authentication_user.active_store">
                <label class="form-label">{{ getFilterLabel('sku') }}</label>
                <div class="sku-input-wrapper">
                    <div class="sku-input-container">
                        <input 
                            type="text" 
                            class="form-control sku-input" 
                            v-model="skuInput"
                            placeholder="Buscar por SKU (Enter)"
                            @keydown.enter.prevent="addSkuTag"
                        />
                        <font-awesome-icon 
                            v-if="skuInput !== ''"
                            :icon="['fas', 'times']" 
                            class="clear-icon cursor-pointer"
                            @click="skuInput = ''"
                        />
                    </div>
                </div>
                <div v-if="selectedSkus.length > 0" class="tags-wrapper mt-2">
                    <div class="tags-container">
                        <span 
                            v-for="(sku, index) in selectedSkus.slice(0, 10)" 
                            :key="index"
                            class="tag"
                        >
                            {{ sku }}
                            <font-awesome-icon 
                                :icon="['fas', 'times']" 
                                class="tag-remove-icon"
                                @click="removeSkuTag(index)"
                            />
                        </span>
                        <span v-if="selectedSkus.length > 10" class="tag tag-more" :title="selectedSkus.slice(10).join(', ')">
                            +{{ selectedSkus.length - 10 }} más...
                        </span>
                    </div>
                    <button class="btn-clear-all" @click="clearSkus" title="Eliminar todos">
                        <font-awesome-icon :icon="['fas', 'trash']" />
                    </button>
                </div>
            </div>

            <div class="row g-3 mb-3">
                <div class="col-6" v-if="shouldShowFilter('supplier')">
                    <label class="form-label">{{ getFilterLabel('supplier') }}</label>
                    <v-select
                        :options="proveedores"
                        v-model="localFilters.supplier"
                        :reduce="proveedor => proveedor"
                        :placeholder="`Seleccionar ${getFilterLabel('supplier').toLowerCase()}`"
                        class="custom-v-select"
                        :clearable="true"
                        @input="onClearSelect('supplier', $event)"
                    />
                </div>
                <div class="col-6" v-if="shouldShowFilter('brand')">
                    <label class="form-label">{{ getFilterLabel('brand') }}</label>
                    <v-select
                        :options="marcas"
                        v-model="localFilters.brand"
                        :reduce="marca => marca"
                        :placeholder="`Seleccionar ${getFilterLabel('brand').toLowerCase()}`"
                        class="custom-v-select"
                        :clearable="true"
                        @input="onClearSelect('brand', $event)"
                    />
                </div>
                <div class="col-6" v-if="shouldShowFilter('select_category')">
                    <label class="form-label">{{ getFilterLabel('select_category') }}</label>
                    <filter-categories @getData="getCategory" class="custom-select" :category="localFilters.select_category"/>
                </div>
                <div class="col-6" v-if="shouldShowFilter('product_group')">
                    <label class="form-label">{{ getFilterLabel('product_group') }}</label>
                    <v-select
                        :options="groups"
                        v-model="localFilters.product_group"
                        :reduce="group => group"
                        :placeholder="`Seleccionar ${getFilterLabel('product_group').toLowerCase()}`"
                        class="custom-v-select"
                        :clearable="true"
                        @input="onClearSelect('product_group', $event)"
                    />
                </div>
                <div class="col-6" v-if="shouldShowFilter('product_type')">
                    <label class="form-label">{{ getFilterLabel('product_type') }}</label>
                    <v-select
                        :options="filtros"
                        v-model="localFilters.product_type"
                        :reduce="filtro => filtro"
                        :placeholder="`Seleccionar ${getFilterLabel('product_type').toLowerCase()}`"
                        class="custom-v-select"
                        :clearable="true"
                        @input="onClearSelect('product_type', $event)"
                    />
                </div>
                <div class="col-6" v-if="authentication_user.active_store">
                    <label class="form-label">{{ getFilterLabel('store') }}</label>
                    <v-select
                        :options="clusters"
                        v-model="localFilters.store"
                        label="name"
                        :reduce="store => store.name"
                        :placeholder="`Seleccionar ${getFilterLabel('store').toLowerCase()}`"
                        class="custom-v-select"
                        :clearable="true"
                        @input="onClearSelect('store', $event)"
                    />
                </div>
            </div>

            <div class="divider-line"></div>

            <div class="active-filters-list" v-if="activeFilters.length > 0">
                <span 
                    v-for="(filter, index) in activeFilters" 
                    :key="index"
                    class="filter-chip"
                >
                    <span class="filter-chip-label">{{ filter.label }}</span>
                    <span class="filter-chip-value">{{ filter.value }}</span>
                </span>
            </div>
            <div v-else class="no-filters-msg">
                <span class="text-secondary size-13">Se deben aplicar filtros</span>
            </div>

            <div class="divider-line"></div>

            <div class="row g-3 mb-4">
                <div class="col-4">
                    <label class="form-label fw-600 size-13 text-secondary mb-2">{{(shouldShowAsRecommender().serviceTypeRecommender || shouldShowAsRecommender().neitherService)  ? 'MARGEN' : 'MARKUP'}} MÍNIMO</label>
                    <div class="input-group">
                        <input 
                            type="number" 
                            class="form-control fw-600 size-16" 
                            v-model.number="marginMin"
                            min="0" 
                            max="100" 
                            step="0.1"
                            :class="{'is-invalid': marginMin > marginMax}"
                        />
                        <span class="input-group-text bg-light border-start-0 text-secondary fw-500">%</span>
                    </div>
                    <small class="text-secondary size-12 mt-2 d-block">Bajo este valor → alerta crítica</small>
                </div>
                <div class="col-4">
                    <label class="form-label fw-600 size-13 text-secondary mb-2">{{(shouldShowAsRecommender().serviceTypeRecommender || shouldShowAsRecommender().neitherService)  ? 'MARGEN' : 'MARKUP'}} OBJETIVO</label>
                    <div class="input-group">
                        <input 
                            type="number" 
                            class="form-control fw-600 size-16" 
                            v-model.number="marginObjective"
                            min="0" 
                            max="100" 
                            step="0.1"
                        />
                        <span class="input-group-text bg-light border-start-0 text-secondary fw-500">%</span>
                    </div>
                    <small class="text-secondary size-12 mt-2 d-block">El motor apunta a este margen</small>
                </div>
                <div class="col-4">
                    <label class="form-label fw-600 size-13 text-secondary mb-2">{{(shouldShowAsRecommender().serviceTypeRecommender || shouldShowAsRecommender().neitherService)  ? 'MARGEN' : 'MARKUP'}} MÁXIMO</label>
                    <div class="input-group">
                        <input 
                            type="number" 
                            class="form-control fw-600 size-16" 
                            v-model.number="marginMax"
                            min="0" 
                            max="100" 
                            step="0.1"
                            :class="{'is-invalid': marginMin > marginMax}"
                        />
                        <span class="input-group-text bg-light border-start-0 text-secondary fw-500">%</span>
                    </div>
                    <small class="text-secondary size-12 mt-2 d-block">Sobre este → revisión de competitividad</small>
                </div>
            </div>

            <div class="margin-visual-bar">
                <div class="margin-bar-track">
                    <div class="margin-bar-fill" :style="barFillStyle"></div>
                    <div 
                        class="margin-bar-marker margin-bar-marker--min"
                        :style="{ left: minPosition + '%' }"
                    >
                        <div class="margin-bar-marker-dot margin-bar-marker-dot--min"></div>
                        <span class="margin-bar-marker-label">Mín</span>
                    </div>
                    <div 
                        class="margin-bar-marker margin-bar-marker--max"
                        :style="{ left: maxPosition + '%' }"
                    >
                        <div class="margin-bar-marker-dot margin-bar-marker-dot--max"></div>
                        <span class="margin-bar-marker-label">Máx</span>
                    </div>
                </div>
                <div class="margin-bar-scale">
                    <span>0%</span>
                    <span>25%</span>
                    <span>50%</span>
                    <span>75%</span>
                    <span>100%</span>
                </div>
            </div>
        </div>

        <div class="d-flex justify-content-end gap-2">
            <button class="btn btn-success fw-400 px-4" @click="saveMarginGroup" :disabled="saving">
                <span v-if="saving">Guardando...</span>
                <span v-else>Guardar</span>
            </button>
        </div>
    </div>
</template>

<script>
import { mapActions,mapGetters } from 'vuex';
import configsRecGroupFiltersMixin from '../mixins/configsRecGroupFiltersMixin';
import {  shouldShowAsRecommender } from '../../../../../utils/servicesValidation.js'
export default {
    name: 'config-margin-group',
    mixins: [configsRecGroupFiltersMixin],
    props: {
        propHead: {
            type: Object,
            default: () => ({})
        },
        activeServiceId: {
            type: Number,
            default: 5
        }
    },
    data() {
        return {
            marginMin: 0,
            marginObjective: 50,
            marginMax: 100,
            saving: false,
        };
    },
    computed: {
        ...mapGetters([
            "authentication_user",
        ]),
        minPosition() {
            return Math.max(0, Math.min(100, this.marginMin));
        },
        maxPosition() {
            return Math.max(0, Math.min(100, this.marginMax));
        },
        barFillStyle() {
            const left = Math.max(0, Math.min(100, this.marginMin));
            const right = Math.max(0, Math.min(100, this.marginMax));
            return {
                left: left + '%',
                width: (right - left) + '%'
            };
        }
    },
    methods: {
        ...mapActions(['update']),
        saveMarginGroup() {
            if (this.marginMin > this.marginMax) {
                this.$notification.error('El margen mínimo debe ser menor o igual al máximo.', { timer: 5, showCloseIcn: true });
                return;
            }

            const activeFilterParams = {};
            this.FILTER_KEYS.forEach(key => {
                if (key === 'sku') {
                    if (this.selectedSkus.length > 0) {
                        activeFilterParams[key] = this.selectedSkus.join(',');
                    }
                } else {
                    const val = this.localFilters[key];
                    if (val !== null && val !== undefined && val !== '' && val !== 0) {
                        activeFilterParams[key] = val;
                    }
                }
            });

            this.saving = true;
            const data = {
                margin_min: this.marginMin / 100,
                margin_objective: this.marginObjective / 100,
                margin_max: this.marginMax / 100,
                filters: activeFilterParams,
            };

            this.update({
                url: `/api/products/config_margin/update_group/`,
                params: data,
            }).then(() => {
                this.saving = false;
                this.$emit('refrest');
                this.$notification.success('Márgenes actualizados correctamente para los productos filtrados.', { timer: 5, showCloseIcn: true });
            }).catch((error) => {
                this.saving = false;
                if (error.status == 400) {
                    this.$notification.error(error.data.message || 'Error al guardar los márgenes.', { timer: 5, showCloseIcn: true });
                } else {
                    this.$notification.error('Error al guardar los márgenes.', { timer: 5, showCloseIcn: true });
                }
            });
        },
        /**
         * Verifica si debe mostrar como recomendador (para usar en template)
         */
        shouldShowAsRecommender(user = null) {
            return shouldShowAsRecommender(user || this.authentication_user, this.activeServiceId);
        }
    }
};
</script>

<style scoped>
.config-margin-card {
    background: #ffffff;
}

.filters-summary-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
}

.filters-summary-icon {
    color: #9ca3af;
    font-size: 0.85rem;
}

.filters-summary-title {
    font-size: 0.85rem;
    font-weight: 600;
    color: #6b7280;
}

.active-filters-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 16px;
}

.filter-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #f3f4f6;
    border-radius: 6px;
    padding: 4px 10px;
    font-size: 0.8rem;
}

.filter-chip-label {
    color: #9ca3af;
    font-weight: 500;
}

.filter-chip-value {
    color: #374151;
    font-weight: 600;
}

.no-filters-msg {
    padding: 8px 0 16px;
}

.divider-line {
    height: 1px;
    background: #e5e7eb;
    margin-bottom: 20px;
}

.input-group-text {
    background-color: #f8f9fa !important;
    border-color: #dee2e6 !important;
    font-size: 14px;
}

.form-control:focus {
    border-color: #1AA179;
    box-shadow: 0 0 0 0.2rem rgba(26, 161, 121, 0.25);
}

.form-control.is-invalid {
    border-color: #dc3545;
}

.form-control.is-invalid:focus {
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}

.margin-visual-bar {
    padding: 16px 8px 8px;
    background: #fafafa;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
}

.margin-bar-track {
    position: relative;
    height: 8px;
    background: #e5e7eb;
    border-radius: 4px;
    margin-bottom: 24px;
}

.margin-bar-fill {
    position: absolute;
    height: 100%;
    background: rgba(26, 161, 121, 0.2);
    border-radius: 4px;
}

.margin-bar-marker {
    position: absolute;
    top: -8px;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
}

.margin-bar-marker-dot {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 2px solid #ffffff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.margin-bar-marker-dot--min {
    background: #6b7280;
}

.margin-bar-marker-dot--objective {
    background: #1AA179;
}

.margin-bar-marker-dot--max {
    background: #f59e0b;
}

.margin-bar-marker-label {
    font-size: 0.65rem;
    font-weight: 600;
    color: #6b7280;
    margin-top: 4px;
    white-space: nowrap;
}

.margin-bar-scale {
    display: flex;
    justify-content: space-between;
    padding: 0 2px;
    font-size: 0.65rem;
    color: #9ca3af;
}

.btn-success {
    background-color: #1AA179 !important;
    border-color: #1AA179 !important;
}

.btn-success:hover:not(:disabled) {
    background-color: #158a65 !important;
    border-color: #158a65 !important;
}

.btn-success:disabled {
    opacity: 0.65;
}

.custom-select {
    border-radius: 6px;
    height: 40px;
    font-size: 14px;
}

.form-label {
    font-weight: 400;
    font-size: 16px;
    margin-bottom: 6px;
}

.sku-input-wrapper {
    width: 100%;
}

.sku-input-container {
    display: flex;
    align-items: center;
    background-color: #fff;
    border: 1px solid #ced4da;
    border-radius: 4px;
    height: 40px;
    padding: 0 12px;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.sku-input-container:focus-within {
    border-color: #1AA179;
    box-shadow: 0 0 0 0.2rem rgba(26, 161, 121, 0.25);
}

.sku-input {
    flex: 1;
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    padding: 0 !important;
    font-size: 14px;
    background: transparent;
    color: #495057;
}

.sku-input::placeholder {
    color: #6c757d;
    opacity: 1;
}

.clear-icon {
    color: #6c757d;
    font-size: 14px;
    cursor: pointer;
    margin-left: 8px;
}

.clear-icon:hover {
    color: #495057;
}

.tags-wrapper {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    width: 100%;
}

.tags-container {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    flex: 1;
}

.tag {
    background-color: #007bff;
    color: white;
    padding: 4px 8px;
    border-radius: 16px;
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.tag-more {
    background-color: #6c757d;
    cursor: default;
}

.tag-remove-icon {
    cursor: pointer;
    font-size: 12px;
    opacity: 0.8;
}

.tag-remove-icon:hover {
    opacity: 1;
}

.btn-clear-all {
    background: none;
    border: none;
    color: #dc3545;
    cursor: pointer;
    padding: 6px 4px;
    font-size: 13px;
    opacity: 0.75;
}

.btn-clear-all:hover {
    opacity: 1;
}
</style>
